<div class="create-property-container">
  <mat-card class="create-property-card">
    <mat-card-header>
        <div class="header-container">
            <img src="assets/images/logo_full_900.png" alt="Logo" /><br/>
            <h2>Create a Property Listing</h2>
        </div>
    </mat-card-header>
    <mat-card-content>
        <div style="display: flex;">
            <form [formGroup]="propForm" (keydown.enter)="onEnter($event)" class="form form-container" *ngIf="propForm">
                <mat-stepper [linear]="true" [orientation]="(stepperOrientation | async)!" (selectionChange)="onStepChange($event)" #stepper>
                    <ng-template matStepperIcon="number" let-index="index">
                        @switch (index) {
                            @case (0) { <mat-icon>info</mat-icon> }
                            @case (1) { <mat-icon>home</mat-icon> }
                            @case (2) { <mat-icon>info</mat-icon> }
                            @case (3) { <mat-icon>calendar_month</mat-icon> }
                            @case (4) { <mat-icon>wifi</mat-icon> } 
                            @case (5) { <mat-icon>photo_camera</mat-icon> }
                            @default { {{ index + 1 }} }
                        }
                    </ng-template>

                    <ng-template matStepperIcon="edit">
                        <mat-icon>arrow_forward</mat-icon>
                    </ng-template>

                    <ng-template matStepperIcon="done">
                        <mat-icon>done</mat-icon>
                    </ng-template>

                    <!-- Instructions Step -->
                    <mat-step>
                        <ng-template matStepLabel>Instructions</ng-template>

                        <div class="instructions-layout">
                            <div class="left-map">
                                <div class="speech-bubble">
                                    <h3 class="center" style="margin-bottom: 8px;">Let's list your property</h3>
                                    <p class="bubble-text">
                                        Creating a listing on Horizinns takes just a few minutes. You'll provide your address, basic details, amenities, and photos. 
                                        <strong>After uploading your images and clicking submit, you'll be taken directly to payment.</strong>
                                    </p>
                                </div>
                            </div>

                            <div class="right-info">
                                <div style="background: #f8f9fa; border-left: 4px solid #3f51b5; padding: 16px 20px; margin: 0 0 20px 0; border-radius: 4px;">
                                    <h4 style="margin-top:0; display:flex; align-items:center; gap:8px;">
                                        <mat-icon style="vertical-align: middle;">info</mat-icon> 
                                        How it works
                                    </h4>
                                    <ol style="margin: 12px 0 0 20px; padding:0; line-height: 1.7;">
                                        <li><strong>Address &amp; Details</strong> — Enter location, property type, bedrooms, your nightly rate, and description.</li>
                                        <li><strong>Pricing Rules</strong> (optional) — Add special rates for weekends, holidays, or seasons.</li>
                                        <li><strong>Amenities</strong> — Select everything your guests will love.</li>
                                        <li><strong>Photos</strong> — Upload up to 15 images (first one is featured). Drag to reorder.</li>
                                    </ol>
                                </div>
                                <div style="background: #fff3e0; padding: 16px 20px; border-radius: 6px; margin: 20px 0;">
                                    <strong>💳 One-time $300 annual fee</strong><br>
                                    Your listing is saved as a <strong>draft</strong> until you complete payment. 
                                    Once paid, it goes live immediately and stays active for one full year. 
                                    You keep 100% of your booking income.
                                </div>
                            </div>                            
                        </div>

                        <div style="width:100%; display: flex; margin-top: 16px;">
                            <p style="font-size: 0.95em; color: #666; margin-left: auto; margin-right: auto;" class="center">
                                <strong>Pro tip:</strong> Great photos + clear descriptions = more bookings. You can always edit everything later from your dashboard.
                            </p>
                        </div>

                        <div class="step-actions">
                            <button mat-flat-button 
                                    class="metallic-text-btn-blue" 
                                    type="button" 
                                    matStepperNext>
                                Get Started
                                <mat-icon>arrow_forward_ios</mat-icon>
                            </button>
                        </div>
                    </mat-step>

                    <mat-step [stepControl]="addressGroup">
                        <ng-template matStepLabel>Address</ng-template>

                        <div formGroupName="address">
                            <div class="form-row row-2">
                                <mat-form-field appearance="outline" class="form-field full-width">
                                    <mat-label>Display Name</mat-label>
                                    <input matInput formControlName="displayName" max="155" matToolTipClass="form-helper-tooltip" matTooltipPosition="above" matTooltip="If included your listing will display: Display Name in City, State">
                                </mat-form-field>

                                <mat-form-field appearance="outline" class="form-field">
                                    <mat-label>Timezone</mat-label>
                                    <mat-select formControlName="timezone">
                                        <mat-option [value]="null">Select Timezone</mat-option>
                                        <mat-option *ngFor="let timezone of timezones$ | async" [value]="timezone.id">
                                            {{ timezone.displayName }}
                                        </mat-option>
                                    </mat-select>
                                    <mat-error *ngIf="addressGroup?.get('timezone')?.hasError('required')">
                                        Timezone is required
                                    </mat-error>
                                </mat-form-field>
                            </div>

                            <div class="form-row row-1">
                                <mat-form-field appearance="outline" class="form-field full-width">
                                    <mat-label>Street</mat-label>
                                    <input matInput formControlName="street" autocomplete="address-level1" max="255">
                                    <mat-error *ngIf="addressGroup?.get('street')?.hasError('required')">
                                        Street is required
                                    </mat-error>
                                </mat-form-field>
                            </div>

                            <div class="form-row row-3">
                                <mat-form-field appearance="outline" class="form-field">
                                    <mat-label>City</mat-label>
                                    <input matInput formControlName="city" autocomplete="home city" max="100">
                                    <mat-error *ngIf="addressGroup?.get('city')?.hasError('required')">
                                        City is required
                                    </mat-error>
                                </mat-form-field>

                                <mat-form-field appearance="outline" class="form-field">
                                    <mat-label>State</mat-label>
                                    <mat-select formControlName="state">
                                        <mat-option [value]="null">Select a State</mat-option>
                                        <mat-option *ngFor="let state of states" [value]="state">
                                            {{ state.name }}
                                        </mat-option>
                                    </mat-select>
                                    <mat-error *ngIf="addressGroup?.get('state')?.hasError('required')">
                                        State is required
                                    </mat-error>
                                </mat-form-field>
            
                                <mat-form-field appearance="outline" class="form-field">
                                    <mat-label>Zip Code</mat-label>
                                    <input matInput formControlName="zipCode" autocomplete="postal-code">
                                    <mat-error *ngIf="addressGroup?.get('zipCode')?.hasError('required')">
                                        Zip Code is required
                                    </mat-error>
                                </mat-form-field>
                            </div>
                        </div>

                        <div class="step-actions">
                            <button mat-flat-button 
                                    class="metallic-text-btn-blue" 
                                    type="button"
                                    (click)="validateAddressAndProceed()"
                                    [disabled]="isCheckingAddress">
                                {{ isCheckingAddress ? 'Checking...' : 'Next' }}
                                <mat-icon>arrow_forward_ios</mat-icon>
                            </button>
                        </div>
                    </mat-step>

                    <mat-step [stepControl]="infoGroup">
                        <ng-template matStepLabel>Details</ng-template>

                        <div class="stepper-form-step-section" formGroupName="info">
                            <div class="form-row row-2">
                                <mat-form-field appearance="outline" class="form-field">
                                    <mat-label>Property Type</mat-label>
                                    <mat-select formControlName="propertyType">
                                        <mat-option [value]="null">Select Property Type</mat-option>
                                        <mat-option *ngFor="let type of propertyTypes" [value]="type">
                                            {{ type | titlecase }}
                                        </mat-option>
                                    </mat-select>
                                    <mat-error *ngIf="infoGroup?.get('propertyType')?.hasError('required')">
                                        Property Type is required
                                    </mat-error>
                                </mat-form-field>
                                
                                <mat-form-field appearance="outline" class="form-field">
                                    <mat-label>Square Footage</mat-label>
                                    <input matInput formControlName="sqFt" type="number">
                                    <mat-error *ngIf="infoGroup?.get('sqFt')?.hasError('required')">
                                        Square footage is required
                                    </mat-error>
                                </mat-form-field>
                            </div>

                            <div class="form-row row-3">
                                <mat-form-field appearance="outline" class="form-field">
                                    <mat-label>Number of Bedrooms</mat-label>
                                    <input matInput formControlName="bedrooms" type="number" max="20">
                                    <mat-error *ngIf="infoGroup?.get('bedrooms')?.hasError('required')">
                                        Bedrooms is required
                                    </mat-error>
                                </mat-form-field>
            
                                <mat-form-field appearance="outline" class="form-field">
                                    <mat-label>Sleeps</mat-label>
                                    <input matInput formControlName="sleeps" type="number" max="50">
                                    <mat-error *ngIf="infoGroup?.get('sleeps')?.hasError('required')">
                                        Sleeps is required
                                    </mat-error>
                                </mat-form-field>
                            </div>
                            
                            <div class="form-row row-3">
                                <mat-form-field appearance="outline" class="form-field">
                                    <mat-label>Number of Bathrooms</mat-label>
                                    <input matInput formControlName="bathrooms" type="number" max="50">
                                    <mat-error *ngIf="infoGroup?.get('bathrooms')?.hasError('required')">
                                        Bathrooms is required
                                    </mat-error>
                                </mat-form-field>
            
                                <mat-form-field appearance="outline" class="form-field">
                                    <mat-label>Cost (per night)</mat-label>
                                    <mat-icon matPrefix>attach_money</mat-icon>
                                    <input matInput formControlName="cost" type="number" />
                                    <mat-error *ngIf="infoGroup?.get('cost')?.hasError('required')">
                                        Cost is required
                                    </mat-error>
                                </mat-form-field>
                            </div>
                            
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>Property Description</mat-label>
                                <textarea 
                                    matInput 
                                    formControlName="description" 
                                    rows="4" 
                                    placeholder="Describe your property (amenities, highlights, rules...)"
                                    maxlength="10000">
                                </textarea>
                                <mat-hint align="end">{{ descriptionControl?.value?.length || 0 }}/10000 characters</mat-hint>
                                <mat-error *ngIf="descriptionControl?.hasError('required')">
                                    Description is required
                                </mat-error>
                                <mat-error *ngIf="descriptionControl?.hasError('maxlength')">
                                    Description must be under 10000 characters
                                </mat-error>
                            </mat-form-field>
                        </div>

                        <div class="step-actions">
                            <button mat-flat-button class="metallic-text-btn-blue" type="button" matStepperNext>
                                Next
                                <mat-icon>arrow_forward_ios</mat-icon>
                            </button>
                            <button mat-flat-button class="metallic-text-btn" type="button" matStepperPrevious>Back</button>
                        </div>
                    </mat-step>

                    <mat-step [stepControl]="seasonsGroup">
                        <ng-template matStepLabel>Pricing Rules</ng-template>

                        <div>
                            <div class="section-header">
                                <h3>Advanced Pricing Rules</h3>
                                <p>Set custom rates for specific dates, holidays, or weekends.</p>
                                <button mat-raised-button type="button" (click)="addPricingRule()" color="primary">
                                    <mat-icon>add</mat-icon>
                                    Add Custom Rule
                                </button>
                            </div>

                            <div class="pricing-rules-list" *ngIf="pricingRules.length > 0">
                                @for(p of this.pricingRules; track p) {
                                    <div class="pricing-rule-item">
                                        <span>{{ p.name}}</span>
                                        <span>{{ p.startDate | date: 'MMMM dd, yyyy' }}</span>
                                        <span>{{ p.endDate | date: 'MMMM dd, yyyy' }}</span>
                                        @if(p.daysOfWeek) {
                                            <span>{{ p.daysOfWeek }}</span>
                                        }
                                        @if(p.priceCents) {
                                            <span>{{ (p.priceCents / 100) | currency }}</span>
                                        }
                                    </div>
                                }
                            </div>

                            <div class="step-actions">
                                <button mat-flat-button class="metallic-text-btn-blue" type="button" matStepperNext>
                                    Next
                                    <mat-icon>arrow_forward_ios</mat-icon>
                                </button>
                                <button mat-flat-button class="metallic-text-btn" type="button" matStepperPrevious>Back</button>
                            </div>
                        </div>
                    </mat-step>

                    <mat-step [stepControl]="amenitiesGroup">
                        <ng-template matStepLabel>Amenities</ng-template>
                        
                        <h3>Amenities</h3>
                        <div *ngIf="!isAmenitiesLoaded" class="loading-amenities">
                            <app-loader></app-loader>
                            <span>Loading amenities...</span>
                        </div>

                        <div *ngIf="isAmenitiesLoaded" [formArrayName]="'amenities'">
                            <div class="amenities-section">
                                @for (category of categories; track category) {
                                    <div class="category-header"><h4>{{ category }}</h4></div>
                                    <div class="checkbox-grid">
                                    @for (amenity of amenities; track amenity.name; let globalIndex = $index) {
                                        @if (amenity.category === category) {
                                            <mat-checkbox 
                                            class="amenity-checkbox" 
                                            [formControlName]="globalIndex"
                                            [checked]="amenitiesGroup.at(globalIndex).value" 
                                            [value]="amenity.name">
                                            <span class="amenity-name">{{ amenity.name }}</span>
                                            </mat-checkbox>
                                        }
                                    }
                                </div>
                                }
                            </div>
                        </div>

                        <div class="step-actions">
                            <button mat-flat-button class="metallic-text-btn-blue" type="button" matStepperNext>
                                Next
                                <mat-icon>arrow_forward_ios</mat-icon>
                            </button>
                            <button mat-flat-button class="metallic-text-btn" type="button" matStepperPrevious>Back</button>
                        </div>
                    </mat-step>

                    <!-- Images Step -->
                    <mat-step [stepControl]="imagesArray">
                        <ng-template matStepLabel>Images</ng-template>
                        @if(!isSubmitting) {
                            <h3>Property Images <small>(Max 15, 5MB each)</small></h3>
                        }
                        
                        <!-- Normal upload UI - shown when NOT submitting -->
                        <div *ngIf="!isSubmitting" formArrayName="images" style="margin-bottom: 24px;">                                
                            <div class="image-upload-container" 
                                (dragover)="onDragOver($event)" 
                                (drop)="onDrop($event)">
                                
                                <input 
                                    #imageInput 
                                    type="file" 
                                    multiple 
                                    accept="image/*" 
                                    (change)="onImagesSelected($event)"
                                    style="display: none">
                                
                                <button 
                                    mat-raised-button 
                                    color="accent" 
                                    type="button"
                                    (click)="imageInput.click()"
                                    [disabled]="images.length >= maxImages">
                                    <mat-icon>photo</mat-icon>
                                    {{ images.length >= maxImages ? 'Max Reached' : 'Add Images' }}
                                </button>
                                
                                <p class="upload-hint">
                                    or drag & drop • {{ images.length }}/{{ maxImages }}
                                </p>
                            </div>

                            @if (imagePreviews.length > 0) {
                                <p>Reorder your images in the arrangement that you'd like them to appear. The first image will be the listing's featured image.</p>
                                
                                <div 
                                  cdkDropList 
                                  cdkDropListOrientation="mixed" 
                                  class="image-preview-grid" 
                                  (cdkDropListDropped)="onDropImage($event)">
                                    
                                    @for (preview of imagePreviews; track preview; let i = $index) {
                                        <div class="image-preview-item" cdkDrag>
                                            @if (i === 0) {
                                                <div class="featured-badge">
                                                    Featured
                                                </div>
                                            }
                                            
                                            <div class="drag-handle" cdkDragHandle>
                                                <mat-icon>drag_indicator</mat-icon>
                                            </div>

                                            <img [src]="preview" alt="Preview {{ i + 1 }}" />
                                            
                                            <div class="image-footer">
                                                <span class="order-number">#{{ i + 1 }}</span>
                                                <button mat-icon-button color="warn" (click)="removeImage($event, i)">
                                                <mat-icon>delete</mat-icon>
                                                </button>
                                            </div>
                                        </div>
                                    }
                                </div>
                            }

                            <div *ngIf="isProcessingImages" class="loading-container">
                                <app-loader class="margin-auto"></app-loader>
                                <span>Processing {{ processedImages }}/{{ totalImages }} images...</span>
                            </div>

                            <mat-error *ngIf="images.length === 0 && this.submitted">
                                At least 1 image is required
                            </mat-error>
                        </div>

                        <!-- PROGRESS UI (shown during submission) -->
                        <div *ngIf="isSubmitting" class="submission-progress">
                            <div class="progress-header">
                                <h3>Creating your listing...</h3>
                                <p class="hint">This may take 20–60 seconds depending on the number of photos.</p>
                            </div>

                            <mat-progress-bar 
                                mode="determinate" 
                                [value]="submissionProgressPercent"
                                color="primary">
                            </mat-progress-bar>
                            <div class="progress-text">{{ submissionProgressPercent }}% complete</div>

                            <div class="steps-checklist">
                                @for (step of submissionSteps; track step.id) {
                                    <div class="step-item" 
                                        [class.active]="step.status === 'active'"
                                        [class.completed]="step.status === 'completed'"
                                        [class.error]="step.status === 'error'">
                                        
                                        <div class="step-icon">
                                            @switch (step.status) {
                                                @case ('completed') { 
                                                    <mat-icon class="success">check_circle</mat-icon> 
                                                }
                                                @case ('active')    { 
                                                    <mat-progress-spinner 
                                                        diameter="24" 
                                                        mode="indeterminate"
                                                        color="primary">
                                                    </mat-progress-spinner> 
                                                }
                                                @case ('error')     { 
                                                    <mat-icon class="error">error</mat-icon> 
                                                }
                                                @default            { 
                                                    <mat-icon class="pending">radio_button_unchecked</mat-icon> 
                                                }
                                            }
                                        </div>
                                        
                                        <div class="step-content">
                                            <span class="label">{{ step.label }}</span>
                                            @if (step.detail) {
                                                <span class="detail">{{ step.detail }}</span>
                                            }
                                        </div>
                                    </div>
                                }
                            </div>

                            <!-- Error recovery -->
                            @if (submissionError) {
                                <div class="error-recovery-card">
                                    <mat-icon color="warn">warning</mat-icon>
                                    <div>
                                        <strong>{{ submissionError }}</strong>
                                        <p>We saved everything we could. You won't lose your work.</p>
                                    </div>

                                    <div class="recovery-actions">
                                        @if (partialPropertyId || propertyCreatedId) {
                                            <button mat-flat-button color="primary" (click)="proceedToPaymentAnyway()">
                                                Proceed to Payment Anyway
                                            </button>
                                            <button mat-stroked-button (click)="goToMyProperties()">
                                                Finish Later from Dashboard
                                            </button>
                                        } @else {
                                            <button mat-flat-button color="primary" (click)="retrySubmission()">
                                                Try Again
                                            </button>
                                        }
                                        <button mat-button (click)="contactSupport()">Contact Support</button>
                                    </div>
                                </div>
                            }

                            @if (successfulSubmission && !submissionError) {
                                <div class="success-banner">
                                    <mat-icon>check_circle</mat-icon>
                                    <span>Success! Taking you to secure checkout...</span>
                                </div>
                            }
                        </div>

                        <!-- Submit button only when NOT submitting -->
                        <div class="step-actions" *ngIf="!isSubmitting">
                            <button 
                                mat-flat-button 
                                class="metallic-text-btn-blue" 
                                type="button" 
                                (click)="submitAndAdvance()" 
                                [disabled]="isProcessing || isProcessingImages || isSubmitting || images.length === 0">
                                
                                {{ (isSubmitting || isProcessing) ? 'Creating Property...' : 'Create Listing & Proceed to Payment' }}
                                
                                <mat-icon *ngIf="!isSubmitting && !isProcessing">arrow_forward</mat-icon>
                            </button>
                            <button mat-flat-button class="metallic-text-btn" type="button" matStepperPrevious>Back</button>
                        </div>
                    </mat-step>
                </mat-stepper>
            </form>
        </div>
    </mat-card-content>
  </mat-card>
</div>